/**
 * Search: one box for a part (code or words, e.g. "alto 9 bronze"), a PO number or a container.
 * A finished-umbrella code points to NetSuite and lists the parts it's made of.
 */
import { useSearchParams } from "react-router-dom";
import { partName } from "../lib/skuNames.ts";
import { fmt, refLabel, search, shortDate, unitOf, useSupply } from "../lib/supply.ts";
import { usePanel } from "../components/DetailPanel.tsx";
import { SearchBox } from "../components/SearchBox.tsx";

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const s = useSupply();
  const { open } = usePanel();
  const r = search(q, s.rows, s.deliveries);

  const deliveriesOf = (sku: string) => s.bySku.get(sku)?.arrivals ?? [];
  const onePart = r.parts.length === 1 ? r.parts[0] : null;

  return (
    <div className="u-page narrow">
      <SearchBox big initial={q} autoFocus={!q} />
      {s.loading && q && <div className="muted">Searching…</div>}
      {!s.loading && q && !r.umbrella && !r.parts.length && !r.deliveries.length && (
        <div className="banner">Nothing matches “{q}”. Try a part code (ALTO908-117), words (alto 9 bronze), a PO number or a container (CA-27013).</div>
      )}

      {r.umbrella && (
        <>
          <div className="banner"><b>That's a finished umbrella.</b> Finished umbrellas are in NetSuite. Its parts are here:</div>
          <div className="results">
            {[r.umbrella.frameSku, r.umbrella.coverSku].map((sku) => {
              const row = s.bySku.get(sku);
              return (
                <button key={sku} type="button" className="res" onClick={() => row && open("part", sku)} disabled={!row}>
                  <div>
                    <div className="t">{sku}</div>
                    <div className="m">{row ? partName(sku) || "Tracked part" : "Not tracked here"}</div>
                  </div>
                  {row && <span className="chev">›</span>}
                </button>
              );
            })}
          </div>
        </>
      )}

      {r.deliveries.length > 0 && (
        <>
          <p className="count">{r.deliveries.length} PO or container{r.deliveries.length > 1 ? "s" : ""}</p>
          <div className="results">
            {r.deliveries.slice(0, 20).map((d) => (
              <button key={d.ref} type="button" className="res" onClick={() => open(d.kind === "po" ? "po" : "ca", d.ref)}>
                <div>
                  <div className="t">{refLabel(d.ref, d.kind)}{d.kind === "container" && <span className="sub"> container</span>}</div>
                  <div className="m">
                    {d.late.length ? `${d.late.length} line${d.late.length > 1 ? "s" : ""} late · oldest due ${shortDate(d.oldestLate)}` : `${d.lines.length} open line${d.lines.length > 1 ? "s" : ""} · next ${shortDate(d.next)}`}
                    {d.later.length > 0 && d.late.length > 0 && ` · ${d.later.length} not due yet`}
                  </div>
                </div>
                <div className="rt">{d.late.length ? <span className="badge late">{d.daysLate} days late</span> : null}</div>
                <span className="chev">›</span>
              </button>
            ))}
          </div>
        </>
      )}

      {r.parts.length > 0 && (
        <>
          <p className="count">{r.parts.length === 50 ? "First 50 parts" : `${r.parts.length} part${r.parts.length > 1 ? "s" : ""}`}</p>
          <div className="results">
            {r.parts.map((p) => (
              <button key={p.itemid} type="button" className="res" onClick={() => open("part", p.itemid)}>
                <div>
                  <div className="t">{p.itemid}</div>
                  <div className="m">{[partName(p.itemid), `${fmt(p.supply.comp_available)} on hand`].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="rt">{p.receipt_overdue ? <span className="badge late">Delivery overdue</span> : null}</div>
                <span className="chev">›</span>
              </button>
            ))}
          </div>
        </>
      )}

      {onePart && deliveriesOf(onePart.itemid).length > 0 && (
        <>
          <p className="count">Deliveries of this part · {deliveriesOf(onePart.itemid).length}</p>
          <div className="results">
            {deliveriesOf(onePart.itemid).map((a, i) => {
              const late = a.date != null && a.date < s.today;
              return (
                <button key={a.ref + i} type="button" className="res" onClick={() => open(a.source === "po" ? "po" : "ca", a.ref)}>
                  <div>
                    <div className="t">{refLabel(a.ref, a.source)}{a.source === "container" && <span className="sub"> container</span>}</div>
                    <div className="m">{fmt(a.qty)} {unitOf(onePart)}{late ? ` · this line was due ${shortDate(a.date)}` : ""}</div>
                  </div>
                  <div className="rt">{late ? <span className="badge late">overdue</span> : a.date ? `${a.source === "container" ? "about " : ""}${shortDate(a.date)}` : "no date"}</div>
                  <span className="chev">›</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
